import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import type { Kid } from '../types';

/** Safety-net poll while the socket is connected — it should never be needed. */
const POLL_CONNECTED_MS = 120_000;
/** Fallback poll when the socket is unavailable (blocked by a proxy, say). */
const POLL_DISCONNECTED_MS = 20_000;

const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30_000;
/** Coalesces a burst of notifications into one fetch. */
const COALESCE_MS = 150;

interface KidStatus {
  kids: Kid[] | null;
  /** True only before the first successful load, so the board never flickers. */
  loading: boolean;
  /** Set when the data on screen is old enough to be worth mentioning. */
  stale: boolean;
  refresh: () => void;
}

function socketUrl(): string {
  const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${scheme}//${window.location.host}/ws`;
}

/**
 * Keeps the board current.
 *
 * A notify-only WebSocket tells us *that* something changed; the statuses
 * themselves always come from GET /api/status. That keeps one code path
 * producing status JSON, and means the socket carries nothing worth
 * authenticating.
 *
 * Polling stays as a safety net: if the socket cannot connect — a corporate
 * proxy that strips Upgrade, say — the board still updates, just less promptly.
 */
export function useKidStatus(): KidStatus {
  const [kids, setKids] = useState<Kid[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [stale, setStale] = useState(false);
  const lastGoodFetch = useRef(0);

  const refresh = useCallback(async (signal?: AbortSignal) => {
    try {
      const { kids: fresh } = await api.getStatus(signal);
      if (signal?.aborted) return;
      setKids(fresh);
      setLoading(false);
      setStale(false);
      lastGoodFetch.current = Date.now();
    } catch {
      if (signal?.aborted) return;
      setLoading(false);
      // One missed poll walking out of wifi range is not worth shouting about;
      // only flag staleness once the data on screen is genuinely old.
      if (lastGoodFetch.current === 0 || Date.now() - lastGoodFetch.current > POLL_DISCONNECTED_MS * 3) {
        setStale(true);
      }
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let socket: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
    let coalesceTimer: ReturnType<typeof setTimeout> | undefined;
    let pollTimer: ReturnType<typeof setInterval> | undefined;
    let attempts = 0;
    let closed = false;

    const load = () => void refresh(controller.signal);

    const schedulePoll = (intervalMs: number) => {
      clearInterval(pollTimer);
      pollTimer = setInterval(load, intervalMs);
    };

    const loadSoon = () => {
      clearTimeout(coalesceTimer);
      coalesceTimer = setTimeout(load, COALESCE_MS);
    };

    const connect = () => {
      if (closed) return;

      let candidate: WebSocket;
      try {
        candidate = new WebSocket(socketUrl());
      } catch {
        scheduleReconnect();
        return;
      }
      socket = candidate;

      candidate.onopen = () => {
        if (closed) return;
        attempts = 0;
        schedulePoll(POLL_CONNECTED_MS);
        // Updates may have happened while we were disconnected.
        load();
      };

      // The frame is only a cue — its contents are deliberately not read.
      candidate.onmessage = () => {
        if (!closed) loadSoon();
      };

      candidate.onclose = () => {
        if (closed || socket !== candidate) return;
        schedulePoll(POLL_DISCONNECTED_MS);
        scheduleReconnect();
      };

      // An error is always followed by close, which handles reconnection.
      candidate.onerror = () => {};
    };

    const scheduleReconnect = () => {
      if (closed || reconnectTimer) return;
      const delay = Math.min(RECONNECT_BASE_MS * 2 ** attempts, RECONNECT_MAX_MS);
      // Jitter keeps every neighbour's phone from reconnecting in lockstep
      // after a deploy.
      const jittered = Math.round(delay * (0.5 + Math.random() / 2));
      attempts += 1;
      reconnectTimer = setTimeout(() => {
        reconnectTimer = undefined;
        connect();
      }, jittered);
    };

    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      load();
      // A socket that died while the tab was hidden should come back now
      // rather than waiting out the backoff.
      if (!socket || socket.readyState === WebSocket.CLOSED) {
        clearTimeout(reconnectTimer);
        reconnectTimer = undefined;
        attempts = 0;
        connect();
      }
    };

    load();
    schedulePoll(POLL_DISCONNECTED_MS);
    connect();

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onVisible);

    return () => {
      closed = true;
      controller.abort();
      clearTimeout(reconnectTimer);
      clearTimeout(coalesceTimer);
      clearInterval(pollTimer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onVisible);
      if (socket) {
        socket.onclose = null;
        socket.close();
      }
    };
  }, [refresh]);

  return { kids, loading, stale, refresh: () => void refresh() };
}
